import React, { useState, useEffect } from "react";
import { FiUser, FiMail, FiPhone, FiMapPin, FiEdit2, FiSave, FiX, FiLoader, FiAlertCircle } from "react-icons/fi";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";

interface ProfileData {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<ProfileData>({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  });

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Try to fetch parent profile - adjust endpoint as needed
        try {
          const response = await axios.get(`${API_BASE_URL}/parents/profile`, {
            params: { email: user.email },
          });
          if (response.data) {
            setProfileData({
              name: response.data.name || user.name || "",
              email: response.data.email || user.email || "",
              phone: response.data.phone || "",
              address: response.data.address || "",
              city: response.data.city || "",
              state: response.data.state || "",
              zipCode: response.data.zipCode || response.data.zip || "",
            });
          }
        } catch (err: any) {
          // If endpoint doesn't exist, use user data from auth
          console.log("Profile endpoint not available, using auth data");
          setProfileData({
            name: user.name || "",
            email: user.email || "",
            phone: "",
            address: "",
            city: "",
            state: "",
            zipCode: "",
          });
        }
      } catch (err: any) {
        console.error("Error fetching profile:", err);
        setError("Failed to load profile data");
        // Set default from user auth
        setProfileData({
          name: user?.name || "",
          email: user?.email || "",
          phone: "",
          address: "",
          city: "",
          state: "",
          zipCode: "",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Try to save to backend - adjust endpoint as needed
      try {
        await axios.put(`${API_BASE_URL}/parents/profile`, {
          ...profileData,
          email: user?.email || profileData.email,
        });
        setIsEditing(false);
        alert("Profile updated successfully!");
      } catch (err: any) {
        // If endpoint doesn't exist, just update locally
        console.log("Profile update endpoint not available");
        setIsEditing(false);
        alert("Profile updated successfully! (Note: Changes are local only)");
      }
    } catch (err: any) {
      console.error("Error saving profile:", err);
      setError("Failed to save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reload original data
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FiLoader className="mx-auto text-[#1ecab8] mb-4 animate-spin" size={48} />
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">My Profile</h1>
        <p className="text-slate-600">Manage your account information</p>
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">
            {error}
          </div>
        )}
      </div>

      <div className="max-w-3xl">
        {/* Profile Card */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200/80 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1ecab8] to-[#1bb8a6] flex items-center justify-center text-white text-3xl font-semibold">
                {profileData.name.charAt(0)}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">{profileData.name}</h2>
                <p className="text-slate-600">Parent Account</p>
              </div>
            </div>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#1ecab8] text-white rounded-lg font-medium hover:bg-[#1bb8a6] transition-colors"
              >
                <FiEdit2 size={18} />
                Edit Profile
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1ecab8] text-white rounded-lg font-medium hover:bg-[#1bb8a6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <FiLoader className="animate-spin" size={18} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave size={18} />
                      Save
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors"
                >
                  <FiX size={18} />
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Profile Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <FiUser className="inline mr-2" />
                Full Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={profileData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-[#1ecab8]"
                />
              ) : (
                <p className="px-4 py-2 bg-slate-50 rounded-lg text-slate-900">
                  {profileData.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <FiMail className="inline mr-2" />
                Email Address
              </label>
              {isEditing ? (
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-[#1ecab8]"
                />
              ) : (
                <p className="px-4 py-2 bg-slate-50 rounded-lg text-slate-900">
                  {profileData.email}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <FiPhone className="inline mr-2" />
                Phone Number
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-[#1ecab8]"
                />
              ) : (
                <p className="px-4 py-2 bg-slate-50 rounded-lg text-slate-900">
                  {profileData.phone}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <FiMapPin className="inline mr-2" />
                Address
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="address"
                  value={profileData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-[#1ecab8] mb-2"
                />
              ) : (
                <p className="px-4 py-2 bg-slate-50 rounded-lg text-slate-900 mb-2">
                  {profileData.address}
                </p>
              )}
              {isEditing && (
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    name="city"
                    value={profileData.city}
                    onChange={handleChange}
                    placeholder="City"
                    className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-[#1ecab8]"
                  />
                  <input
                    type="text"
                    name="state"
                    value={profileData.state}
                    onChange={handleChange}
                    placeholder="State"
                    className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-[#1ecab8]"
                  />
                  <input
                    type="text"
                    name="zipCode"
                    value={profileData.zipCode}
                    onChange={handleChange}
                    placeholder="ZIP Code"
                    className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-[#1ecab8]"
                  />
                </div>
              )}
              {!isEditing && (
                <p className="px-4 py-2 bg-slate-50 rounded-lg text-slate-900">
                  {profileData.city}, {profileData.state} {profileData.zipCode}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Account Settings */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200/80">
          <h3 className="text-xl font-semibold text-slate-900 mb-6">Account Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Change Password</p>
                <p className="text-sm text-slate-600">Update your account password</p>
              </div>
              <button className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors">
                Change
              </button>
            </div>
            <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Notification Preferences</p>
                <p className="text-sm text-slate-600">Manage how you receive notifications</p>
              </div>
              <button className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors">
                Manage
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

